import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { format } from 'date-fns';
import {
  DocumentPlusIcon,
  DocumentMinusIcon,
  ClipboardDocumentCheckIcon,
  PencilSquareIcon,
  ArrowPathIcon,
  ChatBubbleLeftIcon,
  EnvelopeIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const FormSubmissionHistory = ({ formSubmissionId, isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (isOpen && formSubmissionId) {
      fetchHistory();
    }
  }, [formSubmissionId, isOpen]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/form-submissions/${formSubmissionId}/history`);
      setHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching history:', error);
      setError('Failed to load history');
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'DOCUMENT_ADDED':
        return <DocumentPlusIcon className="h-5 w-5 text-green-500" />;
      case 'DOCUMENT_DELETED':
        return <DocumentMinusIcon className="h-5 w-5 text-red-500" />;
      case 'VERIFICATION_ADDED':
      case 'VERIFICATION_UPDATED':
        return <ClipboardDocumentCheckIcon className="h-5 w-5 text-blue-500" />;
      case 'STATUS_CHANGED':
        return <ArrowPathIcon className="h-5 w-5 text-yellow-500" />;
      case 'FORM_UPDATED':
        return <PencilSquareIcon className="h-5 w-5 text-purple-500" />;
      case 'COMMENT_ADDED':
        return <ChatBubbleLeftIcon className="h-5 w-5 text-gray-500" />;
      case 'EMAIL_SENT':
        return <EnvelopeIcon className="h-5 w-5 text-blue-500" />;
      case 'PROFORMA_INVOICE_CREATED':
        return <CurrencyDollarIcon className="h-5 w-5 text-green-500" />;
      default:
        return null;
    }
  };

  const getActionDescription = (entry) => {
    const { actionType, details } = entry;
    switch (actionType) {
      case 'DOCUMENT_ADDED':
        return `Added document "${details.documentName}" to product "${details.productId?.name || 'Unknown Product'}"`;
      case 'DOCUMENT_DELETED':
        return `Deleted document "${details.documentName}" from product "${details.productId?.name || 'Unknown Product'}"`;
      case 'VERIFICATION_ADDED':
        return `Added verification "${details.verificationTypeId?.label || 'Unknown Verification'}" to product "${details.productId?.name || 'Unknown Product'}"`;
      case 'VERIFICATION_UPDATED':
        return `Updated verification "${details.verificationTypeId?.label || 'Unknown Verification'}" for product "${details.productId?.name || 'Unknown Product'}"`;
      case 'STATUS_CHANGED':
        return `Changed status from "${details.oldValue || 'Unknown'}" to "${details.newValue || 'Unknown'}"`;
      case 'FORM_UPDATED':
        return 'Updated form details';
      case 'COMMENT_ADDED':
        return `Added comment: "${details.comment || ''}"`;
      case 'EMAIL_SENT':
        const emailRecipient = details?.recipientEmail || details?.email || 'Unknown recipient';
        const emailSubject = details?.subject ? ` - ${details?.subject}` : '';
        return `Email sent to ${emailRecipient}${emailSubject}`;
      case 'PROFORMA_INVOICE_CREATED':
        const invoiceNumber = details?.invoiceNumber ? ` (${details?.invoiceNumber})` : '';
        const amount = details?.totalAmount ? ` - $${details?.totalAmount.toFixed(2)}` : '';
        return `Proforma invoice created${invoiceNumber}${amount}`;
      default:
        return 'Unknown action';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Form Submission History</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <span className="sr-only">Close</span>
            ×
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto max-h-[calc(80vh-8rem)]">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500">{error}</p>
              <button 
                onClick={fetchHistory}
                className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Try Again
              </button>
            </div>
          ) : history.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No history found</p>
          ) : (
            <div className="space-y-4">
              {history.map((entry) => (
                <div
                  key={entry._id}
                  className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex-shrink-0">
                    {getActionIcon(entry.actionType)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {getActionDescription(entry)}
                    </p>
                    <div className="mt-1 text-xs text-gray-500 flex items-center space-x-2">
                      <span>{entry.userId?.firstName} {entry.userId?.lastName}</span>
                      <span>•</span>
                      <span>{format(new Date(entry.metadata.timestamp), 'MMM d, yyyy h:mm a')}</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-400">
                      {entry.metadata.userAgent} • {entry.metadata.ipAddress}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormSubmissionHistory; 